<template>
  <div>
    <h1>Manage Divisions</h1>

    <b-button variant="primary" style="margin-bottom:20px;" v-b-modal.modal-crud>New Division</b-button>

    <b-modal id="modal-crud" hide-header hide-footer>
      <h2>{{formTitle}}</h2>
      <hr/>
      <form ref="form" @submit.prevent="save">
        <b-form-group
            label="Division Code" label-for="code-input"
            invalid-feedback="Division Code is required">
          <b-form-input id="code-input"
                        v-model="$v.editedItem.code.$model"
                        :state="$v.editedItem.code.$dirty ? !$v.editedItem.code.$error : null"
                        :disabled="editedIndex  !== -1"
                        :readonly="editedIndex  !== -1"
                        :aria-disabled="editedIndex  !== -1"
                        :aria-readonly="editedIndex  !== -1"
                        aria-describedby="input-2-live-feedback"
                        required>
          </b-form-input>
          <b-form-invalid-feedback id="input-2-live-feedback">
            This is a required field.
          </b-form-invalid-feedback>
          <b-form-text id="code-input-help">Required.</b-form-text>
        </b-form-group>

        <b-form-group
            label="Division Name" label-for="name-input"
            invalid-feedback="Division Name is required">
          <b-form-input id="name-input"
                        v-model="$v.editedItem.name.$model"
                        :state="$v.editedItem.name.$dirty ? !$v.editedItem.name.$error : null"
                        aria-describedby="input-1-live-feedback"
                        required>
          </b-form-input>
          <b-form-invalid-feedback id="input-1-live-feedback">
            This is a required field.
          </b-form-invalid-feedback>
          <b-form-text id="name-input-help">Required.</b-form-text>
        </b-form-group>

        <b-form-group
            label="Bureau" label-for="bureau-select"
            invalid-feedback="Division Name is required">
          <b-form-select id="bureau-select"
                         v-model="$v.editedItem.bureau.$model"
                         value-field=""
                         :state="$v.editedItem.bureau.$dirty ? !$v.editedItem.bureau.$error : null"
                         aria-describedby="select-1-live-feedback"
                         required>
            <template v-slot:first>
              <b-form-select-option :value="null" disabled selected>-- Select --</b-form-select-option>
            </template>
            <option v-for="b in bureaus" :value="b" :key="b.code">{{b.name}}</option>
          </b-form-select>
          <b-form-invalid-feedback id="select-1-live-feedback">
            This is a required field.
          </b-form-invalid-feedback>
          <b-form-text id="bureau-select-help">Required.</b-form-text>
        </b-form-group>
        <b-form-group
            label="Staffing Specialist" label-for="specialist-select"
            invalid-feedback="Staffing Specialist is required">
          <b-form-select id="specialist-select"
                         v-model="$v.editedItem.staffingSpecialist.$model"
                         :state="$v.editedItem.staffingSpecialist.$dirty ? !$v.editedItem.staffingSpecialist.$error : null"
                         aria-describedby="select-2-live-feedback"
                         required>
            <template v-slot:first>
              <b-form-select-option :value="null" disabled selected>-- Select --</b-form-select-option>
            </template>
            <option v-for="s in specialists" :value="s" :key="s.userId">{{s.name}}</option>
          </b-form-select>
          <b-form-invalid-feedback id="select-2-live-feedback">
            This is a required field.
          </b-form-invalid-feedback>
          <b-form-text id="specialist-select-help">Required.</b-form-text>
        </b-form-group>
        <b-form-group
            label="Payroll Specialist" label-for="payroll-specialist-select"
            invalid-feedback="Payroll Specialist is required">
          <b-form-select id="payroll-specialist-select"
                         v-model="$v.editedItem.payrollSpecialist.$model"
                         :state="$v.editedItem.payrollSpecialist.$dirty ? !$v.editedItem.payrollSpecialist.$error : null"
                         aria-describedby="select-2-live-feedback"
                         required>
            <template v-slot:first>
              <b-form-select-option :value="null" disabled selected>-- Select --</b-form-select-option>
            </template>
            <option v-for="p in payrollSpecialists" :value="p" :key="p.userId">{{p.name}}</option>
          </b-form-select>
          <b-form-invalid-feedback id="select-2-live-feedback">
            This is a required field.
          </b-form-invalid-feedback>
          <b-form-text id="payroll-select-help">Required.</b-form-text>
        </b-form-group>

        <BaseEmployeeSelector v-model="editedItem.divisionDirectorUser"
                              label="Division Director"
                              :v="$v.editedItem.divisionDirectorUser"
                              tip="Enter employee’s last or first name or PCN to narrow list"
                              :showPcn="true"/>

        <BaseEmployeeSelector v-model="editedItem.assistantCommissionerUser"
                              label="Assistant Commissioner"
                              :v="$v.editedItem.assistantCommissionerUser"
                              tip="Enter employee’s last or first name or PCN to narrow list"
                              :showPcn="true"/>

        <b-row style="margin-bottom:20px;">
          <b-col>
            <b-form-checkbox v-model="$v.editedItem.active.$model" name="active-switch" data-test="active-switch" switch>
              <b>Active?</b>
            </b-form-checkbox>
          </b-col>
        </b-row>

        <b-button @click="close" style="margin-right:15px;">Cancel</b-button>
        <b-button type="submit" variant="primary" :disabled="$v.editedItem.$invalid">Save</b-button>
      </form>
    </b-modal>

    <b-row>
      <b-col style="text-align: right; margin-right: 20px;">
        <b-form-checkbox v-model="showInactive" name="showinactive-filter" data-test="showinactive-filter" switch>
          <b>Include inactive divisions?</b>
        </b-form-checkbox>
      </b-col>
    </b-row>

    <b-table hover small responsive show-empty
             :fields="fields"
             :items="divisions"
             :busy="isLoading">
      <template #table-caption></template>
      <template v-slot:cell(actions)="row">
        <b-button size="sm" @click="editItem(row.item)" class="mr-1">
          edit
        </b-button>
      </template>

      <template v-slot:cell(active)="data">
        {{ data.item.active ? 'Yes' : 'No' }}
      </template>

      <template v-slot:no-data>
        <v-btn color="primary" @click="initialize">Reset</v-btn>
      </template>

      <template v-slot:empty="scope">
        <p>No divisions found.</p>
      </template>

      <div slot="table-busy" class="text-center text-danger my-2">
        <b-spinner class="align-middle" style="margin-right:5px; margin-top:12px;"></b-spinner>
        <strong>Loading, Please Wait...</strong>
      </div>

    </b-table>
  </div>
</template>

<script>
import axios from 'axios';
import axiosRetry from 'axios-retry';
import { validationMixin } from 'vuelidate'
import pageTitle from '@/mixins/pageTitle'
import { required, maxLength } from 'vuelidate/lib/validators'
import isDivisionUnique from '@/validators/isDivisionUnique.js'

axiosRetry(axios, { retries: 3,
  retryDelay: axiosRetry.exponentialDelay  });

export default {
  title: 'Manage Divisions',
  mixins: [validationMixin, pageTitle],
  data: () => ({
    fields: [
      {key: 'code', sortable: true, label: 'Division Code'},
      {key: 'name', sortable: true, label: 'Division Name'},
      {key: 'bureau.name', sortable: true, label: 'Bureau'},
      {key: 'staffingSpecialist.name', sortable: true, label: 'Staffing Specialist'},
      {key: 'payrollSpecialist.name', sortable: true, label: 'Payroll Specialist'},
      {key: 'divisionDirectorUser.name', sortable: true, label: 'Division Director'},
      {key: 'assistantCommissionerUser.name', sortable: true, label: 'Assistant Commissioner'},
      {key: 'active', sortable: true, label: 'Active?'},
      {key: 'actions', label: 'Actions'}
    ],
    isLoading: false,
    showInactive: false,
    divisions: [],
    bureaus: [],
    specialists: [],
    payrollSpecialists: [],
    editedIndex: -1,
    editedItem: {
      code: null,
      name: null,
      active: true,
      bureau: null,
      staffingSpecialist: null,
      payrollSpecialist: null,
      divisionDirectorUser: {},
      assistantCommissionerUser: {},
    },
    defaultItem: {
      code: null,
      name: null,
      active: true,
      bureau: null,
      staffingSpecialist: null,
      payrollSpecialist: null,
      divisionDirectorUser: {},
      assistantCommissionerUser: {},
    },
  }),

  validations() {
    if (this.editedIndex === -1) {
      return {
        editedItem: {
          code: {
            required,
            maxLength: maxLength(5),
            isDivisionUnique
          },
          name: {
            required,
            maxLength: maxLength(75),
          },
          bureau: {
            required
          },
          staffingSpecialist: {
            required
          },
          payrollSpecialist: {
            required
          },
          active: {},
          divisionDirectorUser: {
            userId: {
              required,
              maxLength: maxLength(25)
            }
          },
          assistantCommissionerUser: {
            userId: {
              required,
              maxLength: maxLength(25)
            }
          }
        }
      }
    } else {
      return {
        editedItem: {
          code: {
            required,
            maxLength: maxLength(5),
          },
          name: {
            required,
            maxLength: maxLength(75),
          },
          bureau: {
            required
          },
          staffingSpecialist: {
            required
          },
          payrollSpecialist: {
            required: true
          },
          active: {},
          divisionDirectorUser: {
            userId: {
              required,
              maxLength: maxLength(25)
            }
          },
          assistantCommissionerUser: {
            userId: {
              required,
              maxLength: maxLength(25)
            }
          }
        }
      }
    }
  },

  computed: {
    formTitle () {
      return this.editedIndex === -1 ? 'New Division' : 'Edit Division';
    }
  },

  watch: {
    divisions() {
      this.isLoading = false;
    },
    showInactive() {
      this.fetchDivisions()
    }
  },

  created () {
    this.initialize();
  },

  methods: {
    fetchDivisions() {
      this.isLoading = true;
      let fullUrl = `${process.env.VUE_APP_API_BASE_URL}/organization/division`
      if (this.showInactive) {
        fullUrl = fullUrl + '/all'
      }
      axios.get(fullUrl)
          .then(response => {
            this.divisions = response.data;
            this.isLoading = false;
          });
    },

    fetchBureaus() {
      axios.get(`${process.env.VUE_APP_API_BASE_URL}/organization/bureau`)
          .then(response => {
            this.bureaus = response.data;
          });
    },

    fetchSpecialists() {
      axios.get(`${process.env.VUE_APP_API_BASE_URL}/organization/specialist`)
          .then(response => {
            this.specialists = response.data;
          });
    },
    fetchPayrollSpecialists() {
      axios.get(`${process.env.VUE_APP_API_BASE_URL}/organization/payrollspecialist`)
          .then(response => {
            this.payrollSpecialists = response.data;
          });
    },

    initialize () {
      this.fetchDivisions();
      this.fetchBureaus();
      this.fetchSpecialists();
      this.fetchPayrollSpecialists();
    },

    editItem (item) {
      this.editedIndex = this.divisions.indexOf(item);
      this.editedItem = Object.assign({}, item);
      this.$bvModal.show('modal-crud');
    },

    deleteItem (item) {
      const index = this.divisions.indexOf(item);
      confirm('Are you sure you want to delete this item?') && this.divisions.splice(index, 1);
    },

    close () {
      this.$bvModal.hide('modal-crud');
      setTimeout(() => {
        this.editedItem = Object.assign({}, this.defaultItem);
        this.editedIndex = -1;
        this.fetchDivisions();
      }, 300)
    },

    save () {
      if (this.editedIndex > -1) {
        axios.put(`${process.env.VUE_APP_API_BASE_URL}/organization/division`, this.editedItem)
            .then(response => {
              this.fetchDivisions();
              this.$bvToast.toast('Division Updated', {
                title: 'Status',
                toaster: 'b-toaster-top-center',
                solid: true,
                variant: 'success',
                appendToast: true
              });
            });

      } else {
        axios.post(`${process.env.VUE_APP_API_BASE_URL}/organization/division`, this.editedItem)
            .then(response => {
              this.fetchDivisions();
              this.$bvToast.toast('Division Saved', {
                title: 'Status',
                toaster: 'b-toaster-top-center',
                solid: true,
                variant: 'success',
                appendToast: true
              });
            });

      }
      this.close();
    },
  }
}
</script>